import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import clientmembershipApiController from "../../services/clientmembership.service";
import HandleError from "../HandleError";
import HandleResponse from "../HandleResponse";
import { contentErrorMessage } from "./contentError";
import { beginClientHistoryRequest, clientHistoryQueryKey, ownsClientHistoryRequest } from "./clientHistoryOwnership";

export const ClientMembershipListViewApi = createAsyncThunk("clientmembership/listview", async (formValues, thunkAPI) => {
  try {
    const resposedata = await clientmembershipApiController
      .view(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "listview"))
      .catch((error) => HandleError(thunkAPI, error, "listview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const ClientMembershipUsageApi = createAsyncThunk("clientmembership/usage", async (formValues, thunkAPI) => {
  try {
    return await clientmembershipApiController
      .usage(formValues)
      .then((response) => HandleResponse(thunkAPI, response, "listview"))
      .catch((error) => HandleError(thunkAPI, error, "listview"));
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

const initialState = {
  listClientId: null,
  listQueryKey: null,
  listRequestId: null,
  usageRequestId: null,
  usageQueryKey: null,
  isListView: [],
  isMembershipListViewError: "",
  isUsageView: { current_page: 1, data: [], next_page_url: null, total: 0 },
  isMembershipUsageError: "",
  isSelectedPackage: "",
  isApiStatus: {
    ClientMembershipListViewApi: "",
    ClientMembershipUsageApi: "",
  },
};

const clearUsage = (state) => {
  state.usageRequestId = null;
  state.usageQueryKey = null;
  state.isUsageView = { current_page: 1, data: [], next_page_url: null, total: 0 };
  state.isMembershipUsageError = "";
  state.isApiStatus.ClientMembershipUsageApi = "";
};

const ownsUsageRequest = (state, action) =>
  state.usageRequestId === action.meta.requestId
  && state.usageQueryKey === clientHistoryQueryKey(action.meta.arg)
  && state.listClientId === Number(action.meta.arg?.client_id)
  && Number(state.isSelectedPackage?.packageInstanceId) === Number(action.meta.arg?.package_instance_id);

const clientmembershipSlice = createSlice({
  name: "clientmembership",
  initialState,
  reducers: {
    reset: () => initialState,
    OpenClientMembershipUsage: (state, action) => {
      state.isSelectedPackage = action.payload;
      clearUsage(state);
    },
    CloseClientMembershipUsage: (state) => {
      state.isSelectedPackage = "";
      clearUsage(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ClientMembershipListViewApi.pending, (state, action) => {
        if (beginClientHistoryRequest(state, action)) {
          state.isSelectedPackage = "";
          clearUsage(state);
        }
        state.isApiStatus = { ...state.isApiStatus, ClientMembershipListViewApi: "loading" };
        state.isMembershipListViewError = "";
      })
      .addCase(ClientMembershipListViewApi.fulfilled, (state, action) => {
        if (!ownsClientHistoryRequest(state, action)) return;
        const previous = state.isListView;
        const isNextPage = previous && previous.current_page < action.payload.current_page;
        state.isListView = isNextPage && Array.isArray(previous.data) && Array.isArray(action.payload.data)
          ? { ...action.payload, data: [...previous.data, ...action.payload.data] }
          : action.payload;
        state.isMembershipListViewError = "";
        state.isApiStatus = { ...state.isApiStatus, ClientMembershipListViewApi: "succeeded" };
      })
      .addCase(ClientMembershipListViewApi.rejected, (state, action) => {
        if (!ownsClientHistoryRequest(state, action)) return;
        state.isApiStatus = { ...state.isApiStatus, ClientMembershipListViewApi: "failed" };
        state.isMembershipListViewError = contentErrorMessage(action, "Unable to load packages.");
      })
      .addCase(ClientMembershipUsageApi.pending, (state, action) => {
        if (state.listClientId !== Number(action.meta.arg?.client_id)
          || Number(state.isSelectedPackage?.packageInstanceId) !== Number(action.meta.arg?.package_instance_id)) return;
        const queryKey = clientHistoryQueryKey(action.meta.arg);
        if (state.usageQueryKey !== queryKey) clearUsage(state);
        state.usageQueryKey = queryKey;
        state.usageRequestId = action.meta.requestId;
        state.isApiStatus = { ...state.isApiStatus, ClientMembershipUsageApi: "loading" };
        state.isMembershipUsageError = "";
      })
      .addCase(ClientMembershipUsageApi.fulfilled, (state, action) => {
        if (!ownsUsageRequest(state, action)) return;
        const previous = state.isUsageView;
        const payload = action.payload?.data && !Array.isArray(action.payload.data) ? action.payload.data : action.payload;
        const isNextPage = previous && Number(previous.current_page) < Number(payload.current_page);
        state.isUsageView = isNextPage && Array.isArray(previous.data) && Array.isArray(payload.data)
          ? { ...payload, data: [...previous.data, ...payload.data] }
          : payload;
        state.isApiStatus = { ...state.isApiStatus, ClientMembershipUsageApi: "succeeded" };
        state.isMembershipUsageError = "";
      })
      .addCase(ClientMembershipUsageApi.rejected, (state, action) => {
        if (!ownsUsageRequest(state, action)) return;
        state.isApiStatus = { ...state.isApiStatus, ClientMembershipUsageApi: "failed" };
        state.isMembershipUsageError = contentErrorMessage(action, "Unable to load package credit usage.");
      });
  },
});
// Action creators are generated for each case reducer function
export const { reset, OpenClientMembershipUsage, CloseClientMembershipUsage } = clientmembershipSlice.actions;
export default clientmembershipSlice.reducer;
